import React, { memo, useCallback, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
    Extrapolation,
    SharedValue,
    interpolate,
    useAnimatedStyle,
    useSharedValue,
} from 'react-native-reanimated';

import { isIndividualEvent, isTeamEvent } from '@/api/events/types/types';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { LinearGradient } from '@/components/linear-gradient/LinearGradient';
import { PlayerAvatar } from '@/components/player-avatar/PlayerAvatar';
import { MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { PlayerLastNameWithMask } from '@/feature/player-profile-sheets/components/PlayerLastNameWithMask';
import { ProfileImageSilhouette } from '@/feature/player-profile-sheets/components/ProfileImageSilhouette';
import { ShieldSkiaCanvas } from '@/feature/player-profile-sheets/components/ShieldSkiaCanvas';
import { getPlayerNameInfo } from '@/feature/player-profile-sheets/utils/player-profile-utils';
import {
    PADDING_HORIZONTAL,
    PADDING_RIGHT_PLAYER_IMAGE,
    getPlayerDisplayDetails,
} from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { CircleTopGlow } from '@/feature/promohub/components/CircleTopGlow';
import { useSkiaFont } from '@/hooks/use-skia-font';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { isCollegeSport, isPGA } from '@/utils/league';
import { isPlayerImgPlaceholder } from '@/utils/player';
import { SkFont } from '@shopify/react-native-skia';
import { useShallow } from 'zustand/react/shallow';

const GLOW_RADIUS = 393;
const BACKGROUND_IMAGE_SIZE = 176;
const BACKGROUND_TEAM_RIGHT_PADDING = 15;
const GLOW_HEIGHT = 168;
const TOP_DISTANCE = EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT;

export const PlayerProfileHeader = memo(
    ({ scrollOffset, playerInfo }: { scrollOffset: SharedValue<number>; playerInfo: PlayerWithTeamAndEvent }) => {
        const { width: screenWidth } = useWindowDimensions();
        const [playerHeaderLayout, setPlayerHeaderLayout] = useState<{ height: number; y: number }>({
            height: 0,
            y: 0,
        });
        const [hasErrorLoadingImage, setHasErrorLoadingImage] = useState(false);
        const { leagueIcon, leagueColor } = useLeagueConfigsStore(useShallow(leagueConfigSelector(playerInfo.league)));
        const isPlaceholderProfileImage = isPlayerImgPlaceholder(playerInfo?.icon) || hasErrorLoadingImage;
        const paddingTopForPlayerProfileImage = isPGA(playerInfo.league) || isPlaceholderProfileImage ? 's4' : 's0';
        const isCBB = playerInfo.league === League.Cbb;
        const glowHeight = useSharedValue(GLOW_HEIGHT);
        const fallbackColor = designSystem.colors.neutral900;
        const topGlowColor = isTeamEvent(playerInfo.event)
            ? playerInfo?.team?.color || fallbackColor
            : leagueColor || fallbackColor;
        const paddingRightForPlayerProfileImage = isCollegeSport(playerInfo.league)
            ? isCBB
                ? 56
                : 42
            : PADDING_RIGHT_PLAYER_IMAGE;
        const paddingRight = isWeb
            ? paddingRightForPlayerProfileImage + PADDING_HORIZONTAL
            : paddingRightForPlayerProfileImage + PADDING_HORIZONTAL;

        const onImageError = useCallback(() => {
            setHasErrorLoadingImage(true);
        }, []);

        const headerOpacityMaskStyle = useAnimatedStyle(() => {
            //The hight of the modal title, showing the player name and title
            const modalTitleHeight = EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT;
            //The mask should be drawn on top of the player header
            const maskBottomPositionY = playerHeaderLayout.height + playerHeaderLayout.y;
            //The mask will shrink as user scrolls up, but not more than the modal title height
            const maskHeight = Math.max(maskBottomPositionY - scrollOffset.value, modalTitleHeight);
            return {
                position: 'absolute',
                right: 0,
                left: 0,
                top: 0,
                backgroundColor: ds.colorValue.background.neutral.base,
                opacity: interpolate(scrollOffset.value, [0, playerHeaderLayout.height], [0, 1], 'clamp'),
                height: maskHeight,
                zIndex: 2,
                transform: [{ translateY: scrollOffset.value }],
            };
        });

        const glowAnimatedStyle = useAnimatedStyle(() => {
            return {
                height: glowHeight.value,
                width: screenWidth,
                position: 'absolute',
                top: 0,
                transform: [
                    {
                        //as user overscrolls down, scale up the glow view to give a nice overscrolling effect
                        //and prevent the sudden cut / change of backgrounds color
                        scale: interpolate(scrollOffset.value, [-200, 0], [4, 1], Extrapolation.CLAMP),
                    },
                ],
            };
        });

        return (
            <Box style={{ paddingTop: TOP_DISTANCE }}>
                <Animated.View style={glowAnimatedStyle}>
                    <CircleTopGlow
                        color={topGlowColor}
                        cx={screenWidth / 2}
                        cy={-GLOW_RADIUS}
                        opacity={0.3}
                        radius={GLOW_RADIUS}
                        blur={115}
                        headerHeight={glowHeight}
                        style={styles.topGlow}
                    />
                </Animated.View>

                <LinearGradient
                    start={{ x: 0, y: 0 }}
                    end={{ x: 0, y: 1 }}
                    colors={[
                        withOpacity(ds.colorValue.background.neutral.base, 0),
                        withOpacity(ds.colorValue.background.neutral.base, 1),
                    ]}
                    positions={[0, 0.6944]}
                    style={styles.linearGradient}
                />
                <Row
                    onLayout={({ nativeEvent }) => {
                        setPlayerHeaderLayout(nativeEvent.layout);
                    }}
                    paddingHorizontal={'s16'}
                    justifyContent={'space-between'}
                    zIndex={2}
                >
                    <PlayerDetails playerInfo={playerInfo} />
                </Row>
                <Box
                    paddingTop={paddingTopForPlayerProfileImage}
                    justifyContent={'flex-start'}
                    style={[
                        {
                            right: paddingRight,
                        },
                        styles.profilePicture,
                        common.zIndex2,
                    ]}
                >
                    <PlayerAvatar playerData={playerInfo} playerAvatarVariant={'profileHeader'} />
                </Box>
                {!isPlaceholderProfileImage ? (
                    <Box
                        paddingTop={paddingTopForPlayerProfileImage}
                        justifyContent={'flex-start'}
                        style={[
                            {
                                right: paddingRight,
                            },
                            styles.profilePicture,
                            common.zIndexNegative1,
                        ]}
                    >
                        <ProfileImageSilhouette source={playerInfo.icon} onError={onImageError} />
                    </Box>
                ) : null}
                <PlayerProfileTeamBackgroundImage playerInfo={playerInfo} leagueIcon={leagueIcon} />
                {/** Drawing a mask that will fade in as the user scrolls the player header outside of the view port */}
                <Animated.View style={headerOpacityMaskStyle} />
            </Box>
        );
    }
);

const fontSupportsText = (text: string, font: SkFont) => font.getGlyphIDs(text).every(id => id !== 0);

const PlayerDetails = ({ playerInfo }: { playerInfo: PlayerWithTeamAndEvent }) => {
    const detailsText = getPlayerDisplayDetails(playerInfo);

    const { mainName, secondaryName, hasSingleName } = getPlayerNameInfo(playerInfo);

    // The masked Skia name (PlayerLastNameWithMask) can't render glyphs the Gamay font lacks
    // (e.g. Cyrillic, CJK), since Skia <Text> has no font fallback. In those cases — and on web —
    // render a regular RN <Text>, which falls back to OS fonts. The font isn't loaded on web.
    const font = useSkiaFont(require('../../../assets/fonts/gamay-extrabold.ttf'), 30);
    const renderNameAsText = isWeb || (!!font && !fontSupportsText(mainName.toUpperCase(), font));

    return (
        <Box marginTop={'s20'} marginBottom={'s16'} flex={1}>
            {hasSingleName ? (
                <SizedBox value={16} />
            ) : (
                <Text
                    variant={'titleMedium'}
                    lineHeight={16}
                    numberOfLines={1}
                    ellipsizeMode={'tail'}
                    textTransform={'uppercase'}
                    fontFamily={'Gamay-CondSemiBold'}
                >
                    {secondaryName}
                </Text>
            )}
            {renderNameAsText ? (
                <Text
                    variant={'displayMedium'}
                    lineHeight={36}
                    fontSize={30}
                    numberOfLines={1}
                    ellipsizeMode={'tail'}
                    textTransform={'uppercase'}
                    fontFamily={'gamay-extrabold'}
                >
                    {mainName}
                </Text>
            ) : (
                <PlayerLastNameWithMask lastName={mainName} />
            )}
            <Row paddingTop="s4" maxWidth={'50%'}>
                <Text variant="bodyMedium" color="neutral300" numberOfLines={1}>
                    {detailsText}
                </Text>
            </Row>
        </Box>
    );
};

const PlayerProfileTeamBackgroundImage = ({
    playerInfo,
    leagueIcon,
}: {
    playerInfo: PlayerWithTeamAndEvent;
    leagueIcon?: string;
}) => {
    return (
        <Box
            paddingBottom={'s20'}
            position={'absolute'}
            right={BACKGROUND_TEAM_RIGHT_PADDING}
            top={24}
            zIndex={-2}
            width={BACKGROUND_IMAGE_SIZE}
            height={BACKGROUND_IMAGE_SIZE}
        >
            {isTeamEvent(playerInfo.event) ? (
                isCollegeSport(playerInfo.league) ? (
                    <ShieldSkiaCanvas
                        primaryColor={playerInfo?.team?.color}
                        secondaryColor={playerInfo?.team?.secondaryColor}
                        width={BACKGROUND_IMAGE_SIZE}
                        height={BACKGROUND_IMAGE_SIZE}
                    />
                ) : (
                    <Image
                        source={{ uri: playerInfo.team?.largeIcon }}
                        style={styles.backgroundImage}
                        resizeMode={'contain'}
                    />
                )
            ) : isIndividualEvent(playerInfo.event) ? (
                <Image source={{ uri: leagueIcon }} style={styles.backgroundImage} resizeMode={'contain'} />
            ) : null}
        </Box>
    );
};

const styles = StyleSheet.create({
    linearGradient: {
        position: 'absolute',
        width: '100%',
        height: 162,
        bottom: 0,
        zIndex: 1,
    },
    backgroundImage: {
        width: BACKGROUND_IMAGE_SIZE,
        height: BACKGROUND_IMAGE_SIZE,
        opacity: 0.08,
    },
    topGlow: {
        top: 0,
        zIndex: 0,
    },
    profilePicture: {
        position: 'absolute',
        top: TOP_DISTANCE,
    },
});
